import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, X, BookOpen, FileText, PlayCircle, ClipboardList,
  Clock, Calendar, Filter, ChevronDown, SlidersHorizontal, Download
} from 'lucide-react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import SEO from '@/components/shared/SEO';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from '@/lib/utils';
import { techSpecs } from '@/data/techSpecs';
import SeoContentBlock from '@/components/shared/SeoContentBlock';
import { RESOURCES_SEO } from '@/content/seoContentCopy';

// Tech specs as resource entries (read-only, generated from techSpecs.js).
// `products` is set to the product category so spec sheets respond to the
// Product Category sidebar filter (e.g. "Carpet Tile", "Anti-Fatigue").
const techSpecResources = techSpecs.map((s) => ({
  id: s.id,
  title: s.title,
  description: `Detailed technical specifications for ${s.shortTitle}. Includes construction, performance ratings, sizing, certifications, and care instructions.`,
  type: 'specification',
  // Use a generic technical-doc thumbnail; falls back gracefully if missing
  image: '/Assets/images/05_Resources/tech-spec-thumbnail.jpg',
  topics: ['Specifications', 'Compliance'],
  industries: s.industries,
  products: [s.category],
  productCategory: s.category,
  readTime: 'PDF',
  publishDate: '2024-01-01',
  tags: ['specifications', 'technical', s.shortTitle.toLowerCase(), s.category.toLowerCase()],
  pdf: s.pdf,
}));

// Maintenance & cleaning guide PDFs (read-only, declarative)
const maintenanceGuideResources = [
  {
    id: 'guide-carpet-tile-maintenance',
    title: 'Carpet Tile — Care & Maintenance Guide',
    description: 'Daily, periodic, and restorative cleaning procedures for commercial carpet tile installations. Covers vacuuming, spot removal, deep extraction, and stain charts.',
    type: 'guide',
    image: '/Assets/images/05_Resources/carpet_tile_maintenance.jpg',
    topics: ['Maintenance', 'Best Practices'],
    industries: ['Commercial', 'Retail', 'Hospitality'],
    products: ['Carpet Tile'],
    productCategory: 'Carpet Tile',
    readTime: 'PDF',
    publishDate: '2024-01-01',
    tags: ['maintenance', 'cleaning', 'care', 'carpet tile'],
    pdf: '/Assets/pdf/maintenance-guides/Carpet-Tile-Guide.pdf',
  },
  {
    id: 'guide-crossguard-carpet-tile',
    title: 'Cross Guard Tile — Care & Maintenance Guide',
    description: 'Product-specific cleaning protocol for Cross Guard Tile. Includes recommended equipment, frequencies, and stain removal guidance for high-traffic environments.',
    type: 'guide',
    image: '/Assets/images/05_Resources/Maintenance-practices.jpg',
    topics: ['Maintenance', 'Best Practices'],
    industries: ['Commercial'],
    products: ['Carpet Tile'],
    productCategory: 'Carpet Tile',
    readTime: 'PDF',
    publishDate: '2024-01-01',
    tags: ['maintenance', 'cleaning', 'cross guard', 'carpet tile'],
    pdf: '/Assets/pdf/maintenance-guides/Crossguard_Carpet-Tile-Guide.pdf',
  },
  {
    id: 'guide-frontrunner-cleaning',
    title: 'Frontrunner Entry System — Cleaning Guide',
    description: 'Step-by-step cleaning and maintenance schedule for Frontrunner aluminum grid entry systems. Preserves matting performance and extends product life.',
    type: 'guide',
    image: '/Assets/images/05_Resources/frontrunner_cleaning.jpg',
    topics: ['Maintenance', 'Installation'],
    industries: ['Commercial', 'Retail', 'Hospitality'],
    products: ['Grid Matting'],
    productCategory: 'Grid Matting',
    readTime: 'PDF',
    publishDate: '2024-01-01',
    tags: ['maintenance', 'cleaning', 'frontrunner', 'entry system', 'grid matting'],
    pdf: '/Assets/pdf/maintenance-guides/Frontrunner-Cleaning-Guide.pdf',
  },
  {
    id: 'guide-snap-trax-care',
    title: 'Snap Trax — Care & Maintenance Guide',
    description: 'Care and cleaning protocol for Snap Trax aluminum grid entry systems. Includes recommended frequencies, equipment, and stain removal guidance.',
    type: 'guide',
    image: '/Assets/images/05_Resources/snaptrax_maintenance.jpg',
    topics: ['Maintenance', 'Best Practices'],
    industries: ['Commercial', 'Retail', 'Hospitality'],
    products: ['Grid Matting'],
    productCategory: 'Grid Matting',
    readTime: 'PDF',
    publishDate: '2024-01-01',
    tags: ['maintenance', 'cleaning', 'care', 'snap trax', 'grid matting'],
    pdf: '/Assets/pdf/maintenance-guides/SnapTraxCare.pdf',
  },
  {
    id: 'guide-s-trax-care',
    title: 'S-Trax — Care & Maintenance Guide',
    description: 'Care and cleaning protocol for S-Trax aluminum grid entry systems. Covers daily, periodic, and restorative cleaning to maximize matting performance.',
    type: 'guide',
    image: '/Assets/images/05_Resources/strax_maintenance.jpg',
    topics: ['Maintenance', 'Best Practices'],
    industries: ['Commercial', 'Retail', 'Hospitality'],
    products: ['Grid Matting'],
    productCategory: 'Grid Matting',
    readTime: 'PDF',
    publishDate: '2024-01-01',
    tags: ['maintenance', 'cleaning', 'care', 's-trax', 'grid matting'],
    pdf: '/Assets/pdf/maintenance-guides/S-TraxCare.pdf',
  },
  {
    id: 'guide-lvt-maintenance',
    title: 'Luxury Vinyl — Care & Maintenance Guide',
    description: 'Cleaning, care, and preventive-maintenance guidelines for commercial luxury vinyl plank (LVP/LVT). Covers daily sweeping, spill response, periodic damp-mopping, and restorative procedures.',
    type: 'guide',
    image: '/Assets/images/05_Resources/lvt_maintenance.jpg',
    topics: ['Maintenance', 'Best Practices'],
    industries: ['Commercial', 'Retail', 'Hospitality'],
    products: ['Luxury Vinyl'],
    productCategory: 'Luxury Vinyl',
    readTime: 'PDF',
    publishDate: '2024-01-01',
    tags: ['maintenance', 'cleaning', 'care', 'luxury vinyl', 'lvp', 'lvt'],
    pdf: '/Assets/pdf/maintenance-guides/Portico_LVT_Maintenance_Guidelines.pdf',
  },
];

// Installation guide PDFs (read-only, declarative)
const installationGuideResources = [
  {
    id: 'guide-lvp-installation',
    title: 'Luxury Vinyl Plank — Installation Manual',
    description: 'Step-by-step installation procedures for commercial LVP/LVT flooring. Covers subfloor prep, acclimation, layout, cutting, adhesive application, and finishing.',
    type: 'guide',
    image: '/Assets/images/05_Resources/lvt_installation.jpg',
    topics: ['Installation', 'Best Practices'],
    industries: ['Commercial', 'Retail', 'Hospitality'],
    products: ['Luxury Vinyl'],
    productCategory: 'Luxury Vinyl',
    readTime: 'PDF',
    publishDate: '2024-01-01',
    tags: ['installation', 'lvp', 'lvt', 'luxury vinyl', 'manual'],
    pdf: '/Assets/pdf/installation-guides/LVP-Installation-Manual.pdf',
  },
  {
    id: 'guide-mat-storage',
    title: 'Mat Storage & Handling Guide',
    description: 'Best-practice guidelines for storing, handling, and preserving commercial matting prior to installation. Prevents creasing, curling, and material damage.',
    type: 'guide',
    image: '/Assets/images/05_Resources/mat_storage.jpg',
    topics: ['Installation', 'Best Practices'],
    industries: ['Commercial', 'Retail', 'Hospitality'],
    products: ['Custom Logo Mats', 'Commercial Mats', 'Grid Matting'],
    productCategory: 'Commercial Mats',
    readTime: 'PDF',
    publishDate: '2024-01-01',
    tags: ['installation', 'storage', 'handling', 'matting', 'logistics'],
    pdf: '/Assets/pdf/installation-guides/Mat-Storage-Guide.pdf',
  },
  {
    id: 'guide-s-trax-plus-installation',
    title: 'S-Trax Plus — Installation Guide',
    description: 'Detailed installation instructions for the S-Trax Plus aluminum grid entry system. Includes layout planning, frame assembly, and matting insertion.',
    type: 'guide',
    image: '/Assets/images/05_Resources/Maintenance-practices.jpg',
    topics: ['Installation', 'Best Practices'],
    industries: ['Commercial', 'Retail', 'Hospitality'],
    products: ['Grid Matting'],
    productCategory: 'Grid Matting',
    readTime: 'PDF',
    publishDate: '2024-01-01',
    tags: ['installation', 's-trax', 's-trax plus', 'entry system', 'grid matting'],
    pdf: '/Assets/pdf/installation-guides/S-TraxPlus_Installation.pdf',
  },
];

// Unified resources data with full metadata
const allResources = [
  {
    id: 4,
    title: 'How to Prevent Slip and Fall Accidents in Retail Environments',
    description: 'Essential safety strategies and flooring solutions to minimize slip and fall risks in retail spaces.',
    type: 'article',
    image: '/Assets/images/05_Resources/RetailStore_AT_Large.jpg',
    topics: ['Safety', 'Compliance'],
    industries: ['Retail'],
    products: ['Non-Slip', 'Grid Matting'],
    readTime: '8 min',
    publishDate: '2024-01-15',
    tags: ['safety', 'slip prevention', 'retail'],
    link: '/resources/how-to-prevent-slip-and-fall-accidents-in-retail'
  },
  ...maintenanceGuideResources,
  ...installationGuideResources,
  ...techSpecResources,
];

// Filter options
const contentTypes = [
  { id: 'guide', label: 'Guides', icon: BookOpen },
  { id: 'article', label: 'Articles', icon: FileText },
  { id: 'video', label: 'Videos', icon: PlayCircle },
  { id: 'specification', label: 'Specifications', icon: ClipboardList }
];

const topics = [
  'Specifications',
  'Product Selection',
  'Installation',
  'Maintenance',
  'Zone Defense',
  'Safety',
  'Sustainability',
  'Compliance',
  'Cost Analysis',
  'Best Practices',
  'Product Overview',
  'How-To'
];

const industries = [
  'Retail',
  'Healthcare',
  'Hospitality',
  'Commercial',
  'All Industries'
];

const products = [
  'Anti-Fatigue',
  'Broadloom',
  'Carpet Tile',
  'Commercial Mats',
  'Custom Logo Mats',
  'Grid Matting',
  'Luxury Vinyl',
  'Non-Slip',
  'Rubber Flooring',
  'Accessories',
  'All Products'
];

const sortOptions = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'az', label: 'A-Z' },
  { value: 'za', label: 'Z-A' }
];

// Filter Section Component
const FilterSection = ({ title, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border-b border-border pb-4 mb-4 last:border-0 last:pb-0 last:mb-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full text-left font-semibold text-sm mb-3"
      >
        {title}
        <ChevronDown className={cn("w-4 h-4 transition-transform", isOpen && "rotate-180")} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Resource Card Component
const ResourceCard = ({ resource, onClick }) => {
  const TypeIcon = contentTypes.find(t => t.id === resource.type)?.icon || FileText;
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      layout
      className="group cursor-pointer"
      onClick={() => onClick(resource)}
      data-testid={`resource-card-${resource.id}`}
    >
      <div className="card-portico overflow-hidden h-full flex flex-col">
        <div className="relative h-40 overflow-hidden">
          <img
            src={resource.image}
            alt={resource.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-smooth"
          />
          {/* Content Type Badge */}
          <div className={cn(
            "absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5",
            resource.type === 'guide' && "bg-blue-500 text-white",
            resource.type === 'article' && "bg-emerald-500 text-white",
            resource.type === 'video' && "bg-purple-500 text-white",
            resource.type === 'specification' && "bg-slate-700 text-white"
          )}>
            <TypeIcon className="w-3 h-3" />
            {resource.type === 'specification' ? 'Spec Sheet' : resource.type.charAt(0).toUpperCase() + resource.type.slice(1)}
          </div>
          {/* PDF download icon overlay for specifications */}
          {resource.type === 'specification' && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900/30 group-hover:bg-slate-900/40 transition-colors">
              <div className="w-12 h-12 bg-white/95 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-md">
                <Download className="w-5 h-5 text-slate-700" />
              </div>
            </div>
          )}
          {/* Play button for videos */}
          {resource.type === 'video' && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
              <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                <PlayCircle className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          )}
        </div>
        <div className="p-4 flex flex-col flex-1">
          <h3 className="font-semibold text-sm mb-2 group-hover:text-primary transition-colors line-clamp-2">
            {resource.title}
          </h3>
          <p className="text-xs text-muted-foreground mb-3 line-clamp-2 flex-1">
            {resource.description}
          </p>
          <div className="flex items-center justify-between text-xs text-muted-foreground mt-auto pt-2 border-t border-border">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {resource.readTime}
              </span>
              {resource.type !== 'specification' && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  {formatDate(resource.publishDate)}
                </span>
              )}
              {resource.type === 'specification' && resource.productCategory && (
                <span className="flex items-center gap-1 text-foreground/70">
                  {resource.productCategory}
                </span>
              )}
            </div>
          </div>
          {/* Topic Tag */}
          {resource.topics[0] && (
            <div className="mt-2">
              <span className="inline-block px-2 py-0.5 bg-muted text-xs rounded-full">
                {resource.topics[0]}
              </span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const ResourcesPage = () => {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedIndustries, setSelectedIndustries] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [sortBy, setSortBy] = useState('newest');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);

  // Apply topic, type, industry & product filter from URL query params
  // (e.g. ?topic=Sustainability, ?type=specification, ?industry=Commercial, ?product=Carpet+Tile)
  useEffect(() => {
    const topic = searchParams.get('topic');
    const type = searchParams.get('type');
    const industry = searchParams.get('industry');
    const product = searchParams.get('product');
    if (topic && !selectedTopics.includes(topic)) {
      setSelectedTopics([topic]);
    }
    if (type && !selectedTypes.includes(type)) {
      setSelectedTypes([type]);
    }
    if (industry && !selectedIndustries.includes(industry)) {
      setSelectedIndustries([industry]);
    }
    if (product && !selectedProducts.includes(product)) {
      setSelectedProducts([product]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle filter selection
  const toggleFilter = (value, selectedArray, setSelectedArray) => {
    if (selectedArray.includes(value)) {
      setSelectedArray(selectedArray.filter(v => v !== value));
    } else {
      setSelectedArray([...selectedArray, value]);
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSelectedTypes([]);
    setSelectedTopics([]);
    setSelectedIndustries([]);
    setSelectedProducts([]);
    setSearchQuery('');
  };

  // Remove single filter
  const removeFilter = (type, value) => {
    switch(type) {
      case 'type': setSelectedTypes(selectedTypes.filter(v => v !== value)); break;
      case 'topic': setSelectedTopics(selectedTopics.filter(v => v !== value)); break;
      case 'industry': setSelectedIndustries(selectedIndustries.filter(v => v !== value)); break;
      case 'product': setSelectedProducts(selectedProducts.filter(v => v !== value)); break;
      default: break;
    }
  };

  // Filter and sort resources
  const filteredResources = useMemo(() => {
    let results = [...allResources];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      results = results.filter(r => 
        r.title.toLowerCase().includes(query) ||
        r.description.toLowerCase().includes(query) ||
        r.tags.some(t => t.toLowerCase().includes(query)) ||
        r.type.toLowerCase().includes(query)
      );
    }

    // Content type filter
    if (selectedTypes.length > 0) {
      results = results.filter(r => selectedTypes.includes(r.type));
    }

    // Topic filter
    if (selectedTopics.length > 0) {
      results = results.filter(r => r.topics.some(t => selectedTopics.includes(t)));
    }

    // Industry filter
    if (selectedIndustries.length > 0) {
      results = results.filter(r => 
        r.industries.some(i => selectedIndustries.includes(i)) ||
        r.industries.includes('All Industries')
      );
    }

    // Product filter
    if (selectedProducts.length > 0) {
      results = results.filter(r => 
        r.products.some(p => selectedProducts.includes(p)) ||
        r.products.includes('All Products')
      );
    }

    // Sort
    switch(sortBy) {
      case 'newest':
        results.sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate));
        break;
      case 'oldest':
        results.sort((a, b) => new Date(a.publishDate) - new Date(b.publishDate));
        break;
      case 'az':
        results.sort((a, b) => a.title.localeCompare(b.title));
        break;
      case 'za':
        results.sort((a, b) => b.title.localeCompare(a.title));
        break;
      default:
        break;
    }

    return results;
  }, [searchQuery, selectedTypes, selectedTopics, selectedIndustries, selectedProducts, sortBy]);

  // Active filters for display
  const activeFilters = useMemo(() => {
    const filters = [];
    selectedTypes.forEach(v => filters.push({ type: 'type', value: v, label: contentTypes.find(t => t.id === v)?.label || v }));
    selectedTopics.forEach(v => filters.push({ type: 'topic', value: v, label: v }));
    selectedIndustries.forEach(v => filters.push({ type: 'industry', value: v, label: v }));
    selectedProducts.forEach(v => filters.push({ type: 'product', value: v, label: v }));
    return filters;
  }, [selectedTypes, selectedTopics, selectedIndustries, selectedProducts]);

  const navigate = useNavigate();

  const handleResourceClick = (resource) => {
    // Open PDF in new tab for any resource with a pdf field (specifications, guides)
    if (resource.pdf) {
      window.open(resource.pdf, '_blank', 'noopener,noreferrer');
      return;
    }
    if (resource.link) {
      navigate(resource.link);
    } else if (resource.type === 'video') {
      setSelectedResource(resource);
      setVideoModalOpen(true);
    }
  };

  // Filter sidebar content (reused for desktop and mobile)
  const FilterContent = () => (
    <>
      {/* Content Type */}
      <FilterSection title="Content Type">
        <div className="space-y-2">
          {contentTypes.map(type => (
            <label key={type.id} className="flex items-center gap-2 cursor-pointer group">
              <Checkbox
                checked={selectedTypes.includes(type.id)}
                onCheckedChange={() => toggleFilter(type.id, selectedTypes, setSelectedTypes)}
              />
              <type.icon className="w-4 h-4 text-muted-foreground group-hover:text-primary" />
              <span className="text-sm group-hover:text-primary">{type.label}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Topic */}
      <FilterSection title="Topic">
        <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
          {topics.map(topic => (
            <label key={topic} className="flex items-center gap-2 cursor-pointer group">
              <Checkbox
                checked={selectedTopics.includes(topic)}
                onCheckedChange={() => toggleFilter(topic, selectedTopics, setSelectedTopics)}
              />
              <span className="text-sm group-hover:text-primary">{topic}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Industry */}
      <FilterSection title="Industry" defaultOpen={false}>
        <div className="space-y-2">
          {industries.map(industry => (
            <label key={industry} className="flex items-center gap-2 cursor-pointer group">
              <Checkbox
                checked={selectedIndustries.includes(industry)}
                onCheckedChange={() => toggleFilter(industry, selectedIndustries, setSelectedIndustries)}
              />
              <span className="text-sm group-hover:text-primary">{industry}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Product Category */}
      <FilterSection title="Product Category" defaultOpen={false}>
        <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
          {products.map(product => (
            <label key={product} className="flex items-center gap-2 cursor-pointer group">
              <Checkbox
                checked={selectedProducts.includes(product)}
                onCheckedChange={() => toggleFilter(product, selectedProducts, setSelectedProducts)}
              />
              <span className="text-sm group-hover:text-primary">{product}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Clear All Button */}
      {activeFilters.length > 0 && (
        <Button variant="outline" size="sm" className="w-full mt-4" onClick={clearAllFilters}>
          Clear All Filters
        </Button>
      )}
    </>
  );

  return (
    <Layout>
      <SEO 
        title="Resources & Guides"
        description="Access expert guides, installation videos, maintenance tips, and industry articles for commercial flooring. Download our free resources to help you make informed decisions."
        canonicalUrl="/resources"
      />
      {/* Page Header */}
      <section className="bg-primary-bg text-primary-foreground py-10 md:py-14">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-3xl md:text-4xl font-bold mb-2">
              Resources
            </h1>
            <p className="text-lg text-primary-foreground/90">
              Explore guides, articles, and videos
            </p>
          </motion.div>
        </div>
      </section>

      {/* Global Search Bar */}
      <section className="py-6 bg-background border-b border-border">
        <div className="container-portico">
          <div className="relative max-w-3xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search resources by title, description, or tags..."
              className="pl-12 pr-4 h-12 text-base"
              data-testid="resource-search"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 bg-muted/30">
        <div className="container-portico">
          <div className="flex flex-col lg:flex-row gap-8">
            
            {/* Desktop Sidebar Filters */}
            <aside className="hidden lg:block w-64 flex-shrink-0">
              <div className="sticky top-24 bg-card rounded-xl border border-border p-5">
                <h2 className="font-semibold text-sm mb-4 flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  Filters
                </h2>
                <FilterContent />
              </div>
            </aside>

            {/* Main Content Area */}
            <div className="flex-1 min-w-0">
              {/* Mobile Filter Button + Results Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-4">
                  {/* Mobile Filter Button */}
                  <Button
                    variant="outline"
                    className="lg:hidden flex items-center gap-2"
                    onClick={() => setMobileFiltersOpen(true)}
                    data-testid="mobile-filter-btn"
                  >
                    <SlidersHorizontal className="w-4 h-4" />
                    Filters
                    {activeFilters.length > 0 && (
                      <span className="ml-1 px-1.5 py-0.5 bg-primary text-primary-foreground text-xs rounded-full">
                        {activeFilters.length}
                      </span>
                    )}
                  </Button>
                  
                  {/* Results Count */}
                  <p className="text-sm text-muted-foreground">
                    Showing <span className="font-semibold text-foreground">{filteredResources.length}</span> Resources
                  </p>
                </div>

                {/* Sort Dropdown */}
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[160px]" data-testid="sort-select">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    {sortOptions.map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Active Filters Tags */}
              {activeFilters.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-6">
                  <span className="text-sm text-muted-foreground">Active:</span>
                  {activeFilters.map((filter, idx) => (
                    <button
                      key={`${filter.type}-${filter.value}-${idx}`}
                      onClick={() => removeFilter(filter.type, filter.value)}
                      className="flex items-center gap-1 px-2.5 py-1 bg-primary/10 text-primary text-xs rounded-full hover:bg-primary/20 transition-colors"
                    >
                      {filter.label}
                      <X className="w-3 h-3" />
                    </button>
                  ))}
                  <button
                    onClick={clearAllFilters}
                    className="text-xs text-muted-foreground hover:text-primary underline ml-2"
                  >
                    Clear all
                  </button>
                </div>
              )}

              {/* Resource Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                <AnimatePresence mode="popLayout">
                  {filteredResources.map(resource => (
                    <ResourceCard 
                      key={resource.id} 
                      resource={resource} 
                      onClick={handleResourceClick}
                    />
                  ))}
                </AnimatePresence>
              </div>

              {/* No Results */}
              {filteredResources.length === 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-16"
                >
                  <Search className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold text-lg mb-2">No resources found</h3>
                  <p className="text-muted-foreground mb-4">
                    Try adjusting your search or filters to find what you're looking for.
                  </p>
                  <Button variant="outline" onClick={clearAllFilters}>
                    Clear All Filters
                  </Button>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Filters Panel */}
      <Dialog open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
        <DialogContent className="max-w-md h-[85vh] flex flex-col p-0">
          <DialogHeader className="p-4 border-b border-border">
            <DialogTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Filters
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-4">
            <FilterContent />
          </div>
          <div className="p-4 border-t border-border">
            <Button className="w-full" onClick={() => setMobileFiltersOpen(false)}>
              Show {filteredResources.length} Results
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Modal */}
      <Dialog open={videoModalOpen} onOpenChange={setVideoModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedResource?.title}</DialogTitle>
          </DialogHeader>
          <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
            <div className="text-center">
              <PlayCircle className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                Video playback coming soon.
              </p>
            </div>
          </div>
          {selectedResource && (
            <p className="text-sm text-muted-foreground mt-2">
              {selectedResource.description}
            </p>
          )}
        </DialogContent>
      </Dialog>
      <SeoContentBlock {...RESOURCES_SEO} />
    </Layout>
  );
};

export default ResourcesPage;
